import store from '../state';
import userService from './userService';

// Get API URL from environment variables with fallback
const API_URL = process.env.REACT_APP_USER_API_URL || 'http://localhost:5002';
console.log('ConnectionService: API_URL from environment:', API_URL);

// Mock data for offline/development mode
const MOCK_DATA = {
  connections: [
    { 
      id: 'mock-conn-1', 
      user: { 
        id: 'mock-user-1', 
        name: 'John Doe', 
        imageUrl: 'https://via.placeholder.com/150', 
        email: 'john@example.com',
        accountType: 'personal' 
      }, 
      type: 'friend', 
      status: 'accepted', 
      createdAt: new Date().toISOString()
    },
    { 
      id: 'mock-conn-2', 
      user: { 
        id: 'mock-user-2', 
        name: 'Jane Smith', 
        imageUrl: 'https://via.placeholder.com/150', 
        email: 'jane@example.com',
        accountType: 'personal' 
      }, 
      type: 'family', 
      status: 'accepted', 
      createdAt: new Date().toISOString() 
    }
  ],
  pendingConnections: [
    { 
      id: 'mock-pending-1', 
      requester: { 
        id: 'mock-user-3', 
        name: 'Alex Johnson', 
        imageUrl: 'https://via.placeholder.com/150',
        email: 'alex@example.com',
        accountType: 'teacher' 
      }, 
      type: 'teacher', 
      status: 'pending', 
      message: 'Would like to connect with you',
      createdAt: new Date().toISOString() 
    }
  ],
  searchResults: [
    { 
      id: 'mock-search-1', 
      name: 'Mary Williams', 
      imageUrl: 'https://via.placeholder.com/150',
      email: 'mary@example.com',
      accountType: 'personal' 
    },
    { 
      id: 'mock-search-2', 
      name: 'Robert Brown', 
      imageUrl: 'https://via.placeholder.com/150',
      email: 'robert@example.com',
      accountType: 'business' 
    }
  ]
};

// Flag to indicate if we should use mock data
let useMockData = false; // Always set to false to use real API

// Function to detect if we're running in a development environment
const isDevelopmentEnvironment = () => {
  return process.env.NODE_ENV === 'development' || 
         window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1';
};

// Track API failures to auto-switch to mock mode after repeated failures
let apiFailureCount = 0;
const MAX_API_FAILURES = 3;

// Helper function for fetch requests with better error handling
const fetchWithErrorHandling = async (url, options = {}) => {
  // Ensure URL is absolute by prepending API_URL if needed
  let finalUrl = url;
  if (!url.startsWith('http')) {
    finalUrl = `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  }
  
  try {
    // Get the current auth state from the Redux store
    const state = store.getState();
    const token = state.auth?.token;
    
    // Log request details
    console.log(`ConnectionService: Making API request to: ${finalUrl}`, { 
      method: options.method || 'GET',
      headers: {...options.headers, Authorization: token ? 'Bearer <token>' : undefined}
    });
    
    // Prepare headers with token if available
    const headers = {
      'Content-Type': 'application/json',
      'Api-Name': 'user-api',
      ...options.headers
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 seconds timeout
    
    try {
      // Make the fetch request with timeout
      const response = await fetch(finalUrl, {
        ...options,
        headers,
        signal: controller.signal,
        mode: 'cors' // Explicit CORS mode
      });
      
      clearTimeout(timeoutId);
      
      // Handle non-2xx responses
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        
        console.error(`ConnectionService: API error ${response.status} ${response.statusText}:`, errorData);
        console.error(`ConnectionService: Failed request URL: ${finalUrl}`);
        
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
      }
      
      // Reset failure count on success
      apiFailureCount = 0;
      
      // Parse response body
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }
      
      console.log(`ConnectionService: API response success from ${finalUrl}:`, {
        status: response.status,
        dataType: typeof data,
        isArray: Array.isArray(data),
        hasUsers: data && data.users ? data.users.length : 'no users property'
      });
      
      return data;
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Handle timeouts
      if (error.name === 'AbortError') {
        console.warn('ConnectionService: Request timed out');
        throw new Error('Request timed out');
      }
      
      throw error;
    }
  } catch (error) {
    console.error('ConnectionService: Fetch error:', error);
    throw error;
  }
};

// User Connections API

/**
 * Get user's connections
 * @param {string} userId - User ID
 * @param {string} type - Connection type (family, friend, teacher, all)
 * @returns {Promise<Array>} - List of connections
 */
export const getUserConnections = async (userId, type = 'all') => {
  if (!userId) {
    throw new Error('User ID is required to fetch connections');
  }
  
  try {
    let url = `/connections?userId=${userId}`;
    if (type !== 'all') {
      url += `&type=${type}`;
    }
    
    const response = await fetchWithErrorHandling(url);
    console.log('API response in getUserConnections:', response);
    
    // Check if response has connections property
    if (response.connections) {
      return response;
    }
    
    // Check if response is the connections array directly
    if (Array.isArray(response)) {
      return response;
    }
    
    // Check if response has a status property but no connections (may be empty)
    if (response.status === 'success') {
      return response;
    }
    
    // Default fallback - wrap in standard format
    return {
      connections: Array.isArray(response) ? response : [response],
      status: 'success'
    };
  } catch (error) {
    console.error('Failed to get user connections:', error);
    
    // Return empty connections with error status
    return {
      connections: [],
      status: 'error',
      message: error.message
    };
  }
};

/**
 * Get user's pending connection requests
 * @param {string} userId - User ID
 * @param {string} direction - Direction of the request ('incoming' or 'outgoing')
 * @returns {Promise<Array>} - List of pending requests
 */
export const getPendingConnections = async (userId, direction = 'incoming') => {
  if (!userId) {
    throw new Error('User ID is required to fetch pending connections');
  }
  
  try {
    const url = `/connections/pending?userId=${userId}&direction=${direction}`;
    console.log(`ConnectionService: Fetching pending ${direction} connections for user ${userId}`);
    
    const response = await fetchWithErrorHandling(url);
    console.log(`ConnectionService: Raw API response for pending ${direction} connections:`, response);
    
    // If the response is null or undefined, return an empty array
    if (!response) {
      console.warn(`ConnectionService: Null/undefined response for pending ${direction} connections`);
      return [];
    }
    
    // If the response has a 'requests' property with an array, return it
    if (response.requests && Array.isArray(response.requests)) {
      console.log(`ConnectionService: Found ${response.requests.length} ${direction} requests in response.requests`);
      return response;
    }
    
    // If the response is an array, return it wrapped in a 'requests' object
    if (Array.isArray(response)) {
      console.log(`ConnectionService: Response is an array with ${response.length} ${direction} requests`);
      return { requests: response };
    }
    
    // If the response has an _id property (single request object), convert to array
    if (response._id || response.id || response.requestId) {
      console.log(`ConnectionService: Response appears to be a single request object, wrapping in array`);
      return { requests: [response] };
    }
    
    // Check if it's an empty response but with status success (valid empty result)
    if (response.status === 'success') {
      console.log(`ConnectionService: Response indicates success but no requests found`);
      return { requests: [] };
    }
    
    // Default case - response format unknown, return empty array
    console.warn(`ConnectionService: Unexpected response format for pending ${direction} connections:`, response);
    return { requests: [] };
  } catch (error) {
    console.error(`ConnectionService: Failed to get pending ${direction} connections:`, error);
    return { requests: [] };
  }
};

/**
 * Send a connection request to another user
 * @param {string} requesterId - Requester ID
 * @param {string} recipientId - Recipient ID
 * @param {string} requestType - Type of connection (friend, family, business)
 * @param {string} message - Optional message
 * @returns {Promise<Object>} - Connection request data
 */
export const sendConnectionRequest = async (requesterId, recipientId, requestType, message = '') => {
  if (!requesterId || !recipientId) {
    throw new Error('Both requester and recipient IDs are required');
  }
  
  try {
    // Use the correct endpoint that supports POST method
    const url = '/connection_request';
    const data = {
      requesterId,
      recipientId,
      requestType: requestType || 'friend',
      message
    };
    
    console.log('Sending connection request with data:', data);
    
    const response = await fetchWithErrorHandling(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    return response;
  } catch (error) {
    console.error('Failed to send connection request:', error);
    throw error;
  }
};

/**
 * Send batch connection requests to multiple users
 * @param {string} requesterId - Requester ID
 * @param {Array<string>} recipientIds - Array of recipient IDs
 * @param {string} requestType - Type of connection (friend, family, business)
 * @param {string} message - Optional message
 * @returns {Promise<Array>} - Array of connection request data
 */
export const sendBatchConnectionRequests = async (requesterId, recipientIds, requestType, message = '') => {
  if (!requesterId || !Array.isArray(recipientIds) || recipientIds.length === 0) {
    throw new Error('Requester ID and at least one recipient ID are required');
  }
  
  try {
    // Check if backend supports batch requests
    const supportsBatchRequests = false; // Set to true if API supports batch requests
    
    if (supportsBatchRequests) {
      // If backend supports batch requests, send a single API call
      const url = '/connection_request/batch';
      const data = {
        requesterId,
        recipientIds,
        requestType: requestType || 'friend',
        message
      };
      
      console.log('Sending batch connection requests with data:', data);
      
      const response = await fetchWithErrorHandling(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      return response;
    } else {
      // If backend doesn't support batch requests, send individual requests
      console.log(`Sending ${recipientIds.length} individual connection requests`);
      
      // Use Promise.allSettled instead of Promise.all to handle partial failures
      const requests = recipientIds.map(async (recipientId) => {
        try {
          const result = await sendConnectionRequest(requesterId, recipientId, requestType, message);
          return { status: 'fulfilled', value: result, recipientId };
        } catch (error) {
          console.error(`Failed to send request to recipient ${recipientId}:`, error);
          return { status: 'rejected', reason: error.message, recipientId };
        }
      });
      
      const results = await Promise.all(requests);
      
      // Filter out successful requests
      const successfulRequests = results.filter(result => result.status === 'fulfilled');
      
      // Log failures if any
      const failedRequests = results.filter(result => result.status === 'rejected');
      if (failedRequests.length > 0) {
        console.warn(`${failedRequests.length} out of ${recipientIds.length} requests failed:`, failedRequests);
      }
      
      // Return successful results
      return successfulRequests.map(result => result.value);
    }
  } catch (error) {
    console.error('Failed to send batch connection requests:', error);
    throw error;
  }
};

/**
 * Accept a connection request
 * @param {string} requestId - Request ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Accepted connection details
 */
export const acceptConnectionRequest = async (requestId, userId) => {
  if (!requestId) {
    throw new Error('Request ID is required to accept connection');
  }
  
  try {
    const url = `/connection_response`;
    return await fetchWithErrorHandling(url, { 
      method: 'POST',
      body: JSON.stringify({
        requestId,
        userId,
        action: 'accept'
      })
    });
  } catch (error) {
    console.error('Failed to accept connection request:', error);
    throw error;
  }
};

/**
 * Reject a connection request
 * @param {string} requestId - Request ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Rejected request details
 */
export const rejectConnectionRequest = async (requestId, userId) => {
  if (!requestId) {
    throw new Error('Request ID is required to reject connection');
  }
  
  try {
    const url = `/connection_response`;
    return await fetchWithErrorHandling(url, { 
      method: 'POST',
      body: JSON.stringify({
        requestId,
        userId,
        action: 'reject'
      })
    });
  } catch (error) {
    console.error('Failed to reject connection request:', error);
    throw error;
  }
};

/**
 * Remove an existing connection
 * @param {string} connectionId - Connection ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Removed connection details
 */
export const removeConnection = async (connectionId, userId) => {
  if (!connectionId) {
    throw new Error('Connection ID is required to remove connection');
  }
  
  try {
    const url = `/connections/${connectionId}`;
    return await fetchWithErrorHandling(url, { 
      method: 'DELETE',
      body: JSON.stringify({ userId })
    });
  } catch (error) {
    console.error('Failed to remove connection:', error);
    throw error;
  }
};

/**
 * Search for users to connect with
 * @param {string} query - Search query
 * @param {string} userId - Current user ID
 * @returns {Promise<Array>} - List of user search results
 */
export const searchUsers = async (query, userId) => {
  if (!query) {
    console.log('ConnectionService: Empty query, returning empty results');
    return [];
  }
  
  try {
    // Get the current user ID from the store if not provided
    const state = store.getState();
    const currentUserId = userId || state.auth?.user?.id;
    
    if (!currentUserId) {
      console.warn('ConnectionService: No user ID available for search');
      return [];
    }
    
    // Make API request with userId parameter
    const url = `/search?q=${encodeURIComponent(query)}&userId=${currentUserId}`;
    const response = await fetchWithErrorHandling(url);
    
    // Return the users array from the response
    return response.users || [];
  } catch (error) {
    console.error('ConnectionService: Failed to search users:', error);
    return [];
  }
};

// Special function to enable mock mode for testing if needed
export const enableMockMode = () => {
  console.log('ConnectionService: Enabling mock mode');
  useMockData = true;
  
  // Return a cleanup function to disable mock mode
  return () => {
    console.log('ConnectionService: Disabling mock mode');
    useMockData = false;
    apiFailureCount = 0;
  };
};

// Export all functions
export default {
  getUserConnections,
  getPendingConnections,
  sendConnectionRequest,
  sendBatchConnectionRequests,
  acceptConnectionRequest,
  rejectConnectionRequest,
  removeConnection,
  searchUsers,
  enableMockMode
}; 